// SMS Service utility for integrating with Dialpad API
// This would typically be implemented as a backend API endpoint

/**
 * Send SMS notifications to employees about their paystubs
 * @param {Array} recipients - Array of employee objects with phone numbers
 * @param {string} message - SMS message to send
 * @returns {Promise<Object>} Result object with success status and details
 */
export const sendPaystubNotifications = async (recipients, notificationType = 'sms', payrollNumber = null) => {
  try {
    // Use appropriate API based on notification type
    const endpoint = notificationType === 'sms' 
      ? 'https://paystub.roborx.workers.dev/api/send-sms'
      : 'https://paystub.roborx.workers.dev/api/send-email';
      
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recipients,
        sendAll: recipients.length > 1,
        notificationType,
        payrollNumber
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error(`Error sending ${notificationType} notifications:`, error);
    throw error;
  }
};

/**
 * Backend API endpoint implementation example (would be in your backend server)
 * This shows how to integrate with the existing Dialpad API code
 */
export const backendAPIExample = `
// Example backend endpoint (Node.js/Express)
app.post('/api/send-sms', async (req, res) => {
  try {
    const { recipients, message } = req.body;
    
    // Import your Dialpad API configuration
    const dialpadConfig = {
      apiKey: process.env.DIALPAD_API_KEY,
      userId: process.env.DIALPAD_USER_ID,
      senderGroupId: process.env.DIALPAD_SENDER_GROUP_ID,
      fromNumber: "+17186918236"
    };
    
    const results = [];
    
    for (const recipient of recipients) {
      try {
        // Format phone number
        let phoneNumber = recipient.phone;
        if (!phoneNumber.startsWith('+')) {
          phoneNumber = '+1' + phoneNumber.replace(/[^0-9]/g, '');
        }
        
        // Send SMS using Dialpad API
        const response = await fetch('https://dialpad.com/api/v2/sms', {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + dialpadConfig.apiKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            from_number: dialpadConfig.fromNumber,
            to_numbers: [phoneNumber],
            text: message
          })
        });
        
        if (response.ok) {
          results.push({
            employee: recipient.employee_name,
            phone: phoneNumber,
            status: 'sent',
            success: true
          });
        } else {
          results.push({
            employee: recipient.employee_name,
            phone: phoneNumber,
            status: 'failed',
            success: false,
            error: await response.text()
          });
        }
        
        // Rate limiting - wait between requests
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        results.push({
          employee: recipient.employee_name,
          phone: recipient.phone,
          status: 'error',
          success: false,
          error: error.message
        });
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    const failureCount = results.length - successCount;
    
    res.json({
      success: failureCount === 0,
      sent: successCount,
      failed: failureCount,
      results,
      message: \`SMS notifications sent: \${successCount} successful, \${failureCount} failed\`
    });
    
  } catch (error) {
    console.error('SMS API error:', error);
    res.status(500).json({
      success: false,
      message: 'Failed to send SMS notifications',
      error: error.message
    });
  }
});
`;

/**
 * Default message template for paystub notifications
 */
export const getDefaultMessage = (employeeName = null, messageType = 'paystub') => {
  const greeting = employeeName ? `Hi ${employeeName.split(' ')[0]}!` : "Hi!";
  
  switch (messageType) {
    case 'timesheet_confirmation':
      return `${greeting} Your timesheet is ready for review. Please check your hours at https://pay.starside.app and submit any questions by Tuesday. Payroll processes Wednesday, pays Friday. Thank you!`;
    case 'paystub':
    default:
      return `${greeting} You got paid! Please check https://pay.starside.app to view your pay statement for the most recent payroll period. Thank you!`;
  }
};

/**
 * Get message templates
 */
export const getMessageTemplates = () => {
  return {
    paystub: {
      name: "Paystub Available",
      description: "Notify employees their paystub is ready to view",
      template: getDefaultMessage(null, 'paystub')
    },
    timesheet_confirmation: {
      name: "Timesheet Confirmation Reminder",
      description: "Remind staff to confirm their published timesheet",
      template: getDefaultMessage(null, 'timesheet_confirmation')
    }
  };
};

/**
 * Validate phone number format
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) return false;
  
  // Remove all non-digits
  const cleaned = phone.replace(/[^0-9]/g, '');
  
  // Should be 10 digits (US) or 11 digits (with country code)
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'));
};

/**
 * Validate email address format
 */
export const validateEmail = (email) => {
  if (!email) return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Format phone number for Dialpad API
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return null;
  
  const cleaned = phone.replace(/[^0-9]/g, '');
  
  if (cleaned.length === 10) {
    return '+1' + cleaned;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return '+' + cleaned;
  }
  
  return null;
};